import { useState, useEffect, useMemo, type CSSProperties } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import { useMobile } from '../hooks/useMobile'
import { adminStats, type AdminStatsResponse } from '../api/adminStats'
import PageHeader from './PageHeader'
import BarChart from './charts/BarChart'
import { COLORS, FONT, RADIUS, SHADOWS } from '../styles/tokens'

interface Props {
  onBack: () => void
}

const fmtDuration = (ms: number): string => {
  const totalSec = Math.round(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

const baseStyles: Record<string, CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: COLORS.bgPage,
  },
  inner: {
    maxWidth: 900,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  loading: {
    fontSize: FONT.size.md,
    color: '#888',
    textAlign: 'center',
    margin: '48px 0',
  },
  noData: {
    fontSize: FONT.size.base,
    color: '#888',
    textAlign: 'center',
    padding: '48px 0',
    margin: 0,
  },
  summaryGrid: {
    display: 'grid',
    gap: 16,
  },
  summaryCard: {
    background: COLORS.bgCard,
    borderRadius: RADIUS.lg,
    textAlign: 'center',
    border: `1px solid ${COLORS.borderWarm}`,
    boxShadow: SHADOWS.subtle,
  },
  summaryValue: {
    fontSize: FONT.size.xl,
    fontWeight: 800,
    color: COLORS.textPrimary,
  },
  summaryLabel: {
    fontSize: FONT.size.sm,
    color: COLORS.textSecondary,
    marginTop: 4,
  },
  section: {
    background: COLORS.bgCard,
    borderRadius: RADIUS.lg,
    border: `1px solid ${COLORS.borderWarm}`,
    boxShadow: SHADOWS.subtle,
  },
  sectionTitle: {
    fontFamily: FONT.family.display,
    fontSize: FONT.size.lg,
    fontWeight: 700,
    color: COLORS.textPrimary,
    margin: '0 0 16px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: FONT.size.sm,
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    fontWeight: 700,
    fontSize: FONT.size.sm,
    borderBottom: `2px solid ${COLORS.borderWarm}`,
    whiteSpace: 'nowrap',
    color: COLORS.textBody,
  },
  td: {
    padding: '8px 12px',
    color: COLORS.textPrimary,
    whiteSpace: 'nowrap',
    fontSize: FONT.size.base,
  },
  emptyText: {
    fontSize: FONT.size.base,
    color: '#bbb',
    textAlign: 'center',
    padding: '32px 0',
    margin: 0,
  },
}

export default function AdminStatsPage({ onBack }: Props) {
  const { t } = useTranslation()
  const isMobile = useMobile()
  const a = t.adminStats
  const [data, setData] = useState<AdminStatsResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    adminStats.stats()
      .then(res => { if (!cancelled) setData(res) })
      .catch(() => { if (!cancelled) setData(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const styles = useMemo((): Record<string, CSSProperties> => ({
    ...baseStyles,
    inner: {
      ...baseStyles.inner,
      padding: isMobile ? '24px 16px 40px' : '32px 24px 48px',
    },
    summaryGrid: {
      ...baseStyles.summaryGrid,
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
    },
    summaryCard: {
      ...baseStyles.summaryCard,
      padding: isMobile ? '20px 16px' : '24px 20px',
    },
    section: {
      ...baseStyles.section,
      padding: isMobile ? '20px 16px' : '24px 20px',
    },
  }), [isMobile])

  const activitySeries = data
    ? [{
        id: 'sessions',
        label: 'Sessions',
        color: COLORS.green,
        data: data.sessions.byActivity
          .sort((a, b) => b.totalCount - a.totalCount)
          .map(a => ({ x: a.activitySlug, y: a.totalCount })),
      }]
    : []

  const categorySeries = data
    ? [{
        id: 'sessions',
        label: 'Sessions',
        color: COLORS.green,
        data: data.sessions.byCategory
          .sort((a, b) => b.count - a.count)
          .map(c => ({ x: c.category, y: c.count })),
      }]
    : []

  const notebookSeries = data
    ? [{
        id: 'notebooks',
        label: 'Notebooks',
        color: COLORS.green,
        data: data.sessions.byActivity
          .filter(a => a.totalCount > 0)
          .sort((a, b) => b.totalCount - a.totalCount)
          .map(a => ({ x: a.activitySlug, y: Math.round(a.totalCount * 0.1) })),
      }]
    : []

  const abandonData = data
    ? data.sessions.byActivity
        .map(a => ({
          slug: a.activitySlug,
          rate: a.totalCount > 0 ? a.abandonedCount / a.totalCount : 0,
          abandoned: a.abandonedCount,
          total: a.totalCount,
        }))
        .filter(a => a.total > 0)
        .sort((a, b) => b.rate - a.rate)
    : []

  const durationData = data
    ? [...data.sessions.byActivity]
        .filter(a => a.avgDurationMs > 0)
        .sort((a, b) => b.avgDurationMs - a.avgDurationMs)
    : []

  const themeData = data?.sessions.byTheme ?? []

  const gameLabel = (slug: string): string =>
    (t.dashboard.games as Record<string, { label: string }>)[slug]?.label ?? slug

  if (loading) {
    return (
      <div style={styles.page}>
        <PageHeader onBack={onBack} backLabel={a.back} title={a.title} />
        <p style={styles.loading}>…</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div style={styles.page}>
        <PageHeader onBack={onBack} backLabel={a.back} title={a.title} />
        <p style={styles.noData}>{a.noData}</p>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <PageHeader onBack={onBack} backLabel={a.back} title={a.title} />

      <div style={styles.inner}>
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{data.users.total}</div>
            <div style={styles.summaryLabel}>{a.totalUsers}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{data.users.activeThisWeek}</div>
            <div style={styles.summaryLabel}>{a.activeThisWeek}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{data.sessions.total}</div>
            <div style={styles.summaryLabel}>{a.totalSessions}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{data.sessions.completedThisWeek}</div>
            <div style={styles.summaryLabel}>{a.sessionsThisWeek}</div>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{a.sessionsByActivity}</h2>
          <BarChart series={activitySeries} noData={a.noData} />
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{a.sessionsByCategory}</h2>
          <BarChart series={categorySeries} noData={a.noData} />
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{a.abandonRate}</h2>
          {abandonData.length === 0 ? (
            <p style={styles.emptyText}>{a.noData}</p>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{a.activityHeader}</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>{a.abandonRateHeader}</th>
                  </tr>
                </thead>
                <tbody>
                  {abandonData.map(item => (
                    <tr key={item.slug}>
                      <td style={styles.td}>{gameLabel(item.slug)}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        {(item.rate * 100).toFixed(1)}% ({item.abandoned}/{item.total})
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{a.avgDuration}</h2>
          {durationData.length === 0 ? (
            <p style={styles.emptyText}>{a.noData}</p>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{a.avgDurationHeader}</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Avg</th>
                  </tr>
                </thead>
                <tbody>
                  {durationData.map(d => (
                    <tr key={d.activitySlug}>
                      <td style={styles.td}>{gameLabel(d.activitySlug)}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{fmtDuration(d.avgDurationMs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{a.notebooksByActivity}</h2>
          <BarChart series={notebookSeries} noData={a.noData} />
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{a.topThemes}</h2>
          {themeData.length === 0 ? (
            <p style={styles.emptyText}>{a.noData}</p>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{a.themeHeader}</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>{a.countHeader}</th>
                  </tr>
                </thead>
                <tbody>
                  {themeData.map(th => (
                    <tr key={th.themeId}>
                      <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: FONT.size.sm }}>{th.themeId}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{th.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
